"use client"

import { useState } from "react"
import {
  BarChart3,
  FileText,
  TrendingUp,
  Zap,
  Eye,
  ArrowRight,
  Download,
  ChevronLeft,
  ChevronRight,
} from "lucide-react"
import "./ReportsPage.css"
import { useNavigate } from "react-router-dom"
import BackButton from "../shared/backButton"

const ReportsPage = () => {
  const [activeCard, setActiveCard] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [sortBy, setSortBy] = useState("name")
  const [sortOrder, setSortOrder] = useState("asc")
  const itemsPerPage = 5

  // Sample saved reports data
  const savedReports = []

  {/* {
      id: 1,
      name: "Q4 Performance Review",
      type: "Performance Analysis",
      createdDate: "2024-01-15",
      lastModified: "2024-01-20",
      size: "2.4 MB",
      status: "Completed",
    },
    {
      id: 2,
      name: "Engineering Team Skills Gap",
      type: "Skills Analysis",
      createdDate: "2024-01-10",
      lastModified: "2024-01-18",
      size: "1.8 MB",
      status: "Completed",
    },
    {
      id: 3,
      name: "Leadership Pipeline Assessment",
      type: "Leadership Report",
      createdDate: "2024-01-08",
      lastModified: "2024-01-16",
      size: "3.1 MB",
      status: "Completed",
    },
    {
      id: 4,
      name: "Internal Mobility Trends",
      type: "Mobility Analysis",
      createdDate: "2024-01-05",
      lastModified: "2024-01-14",
      size: "2.7 MB",
      status: "Completed",
    },
    {
      id: 5,
      name: "Training Effectiveness Report",
      type: "Training Analysis",
      createdDate: "2024-01-03",
      lastModified: "2024-01-12",
      size: "1.9 MB",
      status: "Completed",
    },
    {
      id: 6,
      name: "Department Comparison Study",
      type: "Comparative Analysis",
      createdDate: "2024-01-01",
      lastModified: "2024-01-10",
      size: "4.2 MB",
      status: "Completed",
    },
    {
      id: 7,
      name: "Employee Satisfaction Survey",
      type: "Engagement Report",
      createdDate: "2023-12-28",
      lastModified: "2024-01-08",
      size: "2.1 MB",
      status: "Completed",
    },
    {
      id: 8,
      name: "Talent Acquisition Analysis",
      type: "Recruitment Report",
      createdDate: "2023-12-25",
      lastModified: "2024-01-06",
      size: "3.5 MB",
      status: "Completed",
    }, */}

  const navigate = useNavigate()
  const onNavigate = (page) => {
    console.log("Navigating to:", page)
    navigate(page)
  }

  const handleCardClick = (cardId) => {
    switch (cardId) {
      case "pregenerated":
        onNavigate("pre-generated")
        break
      case "analysis":
        onNavigate("analysis-comparison")
        break
      case "insights":
        onNavigate("business-insights")
        break
      default:
        break
    }
  }

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc")
    } else {
      setSortBy(column)
      setSortOrder("asc")
    }
    setCurrentPage(1)
  }

  const sortedReports = [...savedReports].sort((a, b) => {
    let aValue = a[sortBy]
    let bValue = b[sortBy]

    if (sortBy === "createdDate" || sortBy === "lastModified") {
      aValue = new Date(aValue)
      bValue = new Date(bValue)
    }

    if (sortOrder === "asc") {
      return aValue > bValue ? 1 : -1
    } else {
      return aValue < bValue ? 1 : -1
    }
  })

  const totalPages = Math.ceil(sortedReports.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedReports = sortedReports.slice(startIndex, startIndex + itemsPerPage)

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  const getSortIcon = (column) => {
    if (sortBy !== column) return null
    return sortOrder === "asc" ? "↑" : "↓"
  }

  return (
    <div className="reports-dashboard-page">
      <BackButton />

      <main className="reports-dashboard-main">
        <div className="container">
          {/* Hero Section */}
          <div className="reports-dashboard-hero">
            <div className="reports-hero-pattern"></div>
            <div className="reports-hero-content-wrapper">
              <div className="reports-hero-icon-wrapper">
                <BarChart3 size={40} />
              </div>
              <div className="reports-hero-text-wrapper">
                <h1>Reports & Analytics</h1>
                <p>Comprehensive reporting suite for data-driven talent intelligence decisions</p>
              </div>
            </div>
          </div>

          {/* Main Content Grid - Stats on Left, Analysis Card on Right */}
          <div className="reports-main-content-grid-unique">
            {/* Stats Grid - Left Side */}
            <div className="reports-stats-section-unique">
              <h2>Quick Statistics</h2>
              <div className="reports-stats-grid-2x2-unique">
                <div className="reports-stat-card-wrapper reports-stat-primary">
                  <div className="reports-stat-icon-wrapper">
                    <FileText size={24} />
                  </div>
                  <div className="reports-stat-info-wrapper">
                    <div className="reports-stat-value-display">25</div>
                    <div className="reports-stat-title-display">Available Reports</div>
                  </div>
                </div>
                <div className="reports-stat-card-wrapper reports-stat-secondary">
                  <div className="reports-stat-icon-wrapper">
                    <BarChart3 size={24} />
                  </div>
                  <div className="reports-stat-info-wrapper">
                    <div className="reports-stat-value-display">150</div>
                    <div className="reports-stat-title-display">Comparisons Made</div>
                  </div>
                </div>
                {/*<div className="reports-stat-card-wrapper reports-stat-tertiary">
                  <div className="reports-stat-icon-wrapper">
                    <TrendingUp size={24} />
                  </div>
                  <div className="reports-stat-info-wrapper">
                    <div className="reports-stat-value-display">89</div>
                    <div className="reports-stat-title-display">Insights Generated</div>
                  </div>
                </div>
                <div className="reports-stat-card-wrapper reports-stat-quaternary">
                  <div className="reports-stat-icon-wrapper">
                    <Zap size={24} />
                  </div>
                  <div className="reports-stat-info-wrapper">
                    <div className="reports-stat-value-display">12</div>
                    <div className="reports-stat-title-display">Active Evaluations</div>
                  </div>
                </div>*/}
              </div>
            </div>

            {/* Analysis & Comparison Card - Right Side */}
            <div className="reports-analysis-card-section-unique">
              <div
                className={`reports-dashboard-main-card reports-secondary ${
                  activeCard === "analysis" ? "reports-card-active" : ""
                }`}
                onClick={() => handleCardClick("analysis")}
                onMouseEnter={() => setActiveCard("analysis")}
                onMouseLeave={() => setActiveCard(null)}
              >
                <div className="reports-card-pattern-bg"></div>
                <div className="reports-card-glow-effect"></div>

                <div className="reports-card-content-wrapper">
                  <div className="reports-card-icon-wrapper">
                    <BarChart3 size={32} />
                  </div>

                  <div className="reports-card-text-content">
                    <h3>Analysis & Comparison</h3>
                    <p>Compare teams, departments, employees, and roles with advanced analytics tools</p>
                  </div>

                  {/*<div className="reports-card-features-list">
                    <div className="reports-feature-item-wrapper">
                      <Eye size={14} />
                      <span>Multi-entity comparison</span>
                    </div>
                    <div className="reports-feature-item-wrapper">
                      <Eye size={14} />
                      <span>Performance benchmarking</span>
                    </div>
                    <div className="reports-feature-item-wrapper">
                      <Eye size={14} />
                      <span>Skills compatibility analysis</span>
                    </div>
                    <div className="reports-feature-item-wrapper">
                      <Eye size={14} />
                      <span>Detailed comparison reports</span>
                    </div>
                  </div>*/}

                  <div className="reports-card-action-wrapper">
                    <span>Explore Analysis & Comparison</span>
                    <ArrowRight size={16} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Saved Reports Section */}
          <div className="saved-reports-section">
            <h2>Saved Reports</h2>
            <div className="saved-reports-table-container">
              <table className="saved-reports-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort("name")} className="sortable-header">
                      Report Name {getSortIcon("name")}
                    </th>
                    {/*<th onClick={() => handleSort("type")} className="sortable-header">
                      Type {getSortIcon("type")}
                    </th>*/}
                    <th onClick={() => handleSort("createdDate")} className="sortable-header">
                      Created Date {getSortIcon("createdDate")}
                    </th>
                    {/*<th onClick={() => handleSort("lastModified")} className="sortable-header">
                      Last Modified {getSortIcon("lastModified")}
                    </th>
                    <th onClick={() => handleSort("size")} className="sortable-header">
                      Size {getSortIcon("size")}
                    </th>
                    <th onClick={() => handleSort("status")} className="sortable-header">
                      Status {getSortIcon("status")}
                    </th>*/}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
  {paginatedReports.length === 0 ? (
    <tr>
      <td colSpan={3} style={{ padding: "12px", textAlign: "center", color: "#6b7280" }}>
        No saved reports
      </td>
    </tr>
  ) : (
    paginatedReports.map((report) => (
      <tr key={report.id}>
        <td>{report.name}</td>
        <td>{new Date(report.createdDate).toLocaleDateString()}</td>
        <td>
          <div className="report-actions">
            <button className="report-action-btn report-view-btn" title="View Report">
              <Eye size={16} />
            </button>
            <button className="report-action-btn report-download-btn" title="Download Report">
              <Download size={16} />
            </button>
          </div>
        </td>
      </tr>
    ))
  )}
</tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="pagination-container">
              <div className="pagination-info">
                Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, sortedReports.length)} of{" "}
                {sortedReports.length} reports
              </div>
              <div className="pagination-controls">
                <button
                  className="pagination-btn"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft size={16} />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    className={`pagination-btn ${currentPage === page ? "active" : ""}`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                ))}
                <button
                  className="pagination-btn"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}

export default ReportsPage
